/**
 * QUI-228 / QUI-233: structured CMS content blocks (FAQ, named sections)
 * stored in site_pages.meta.content_blocks — content the main body editor
 * does not expose. Section blocks may include an optional Pro Tip callout.
 */

export type SitePageFaqItem = {
	id: string;
	q: string;
	a: string;
};

export type SitePageFaqBlock = {
	id: string;
	type: 'faq';
	title: string;
	items: SitePageFaqItem[];
};

export type SitePageSectionTip = {
	title: string;
	body_markdown: string;
};

export type SitePageSectionBlock = {
	id: string;
	type: 'section';
	title: string;
	body_markdown: string;
	/** Optional callout (e.g. Help “Pro Tip”) nested in the section. */
	tip?: SitePageSectionTip | null;
};

export type SitePageContentBlock = SitePageFaqBlock | SitePageSectionBlock;

const BLOCKS_STORAGE_PREFIX = 'quilty.siteCmsContentBlocks:';

export function contentBlocksStorageKey(path: string): string {
	const trimmed = path.trim();
	const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
	return `${BLOCKS_STORAGE_PREFIX}${normalized || '/'}`;
}

export function isFaqBlock(block: SitePageContentBlock): block is SitePageFaqBlock {
	return block.type === 'faq';
}

export function isSectionBlock(
	block: SitePageContentBlock
): block is SitePageSectionBlock {
	return block.type === 'section';
}

function asNonEmptyString(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	return trimmed || null;
}

function parseSectionTip(raw: unknown): SitePageSectionTip | null {
	if (!raw || typeof raw !== 'object') return null;
	const obj = raw as Record<string, unknown>;
	const title = asNonEmptyString(obj.title) ?? '';
	const body =
		typeof obj.body_markdown === 'string'
			? obj.body_markdown
			: typeof obj.body === 'string'
				? obj.body
				: '';
	if (!title.trim() && !body.trim()) return null;
	return { title: title || 'Pro Tip', body_markdown: body };
}

export function parseContentBlocks(raw: unknown): SitePageContentBlock[] {
	if (!Array.isArray(raw)) return [];
	const out: SitePageContentBlock[] = [];
	for (const entry of raw) {
		if (!entry || typeof entry !== 'object') continue;
		const obj = entry as Record<string, unknown>;
		const id = asNonEmptyString(obj.id);
		const type = asNonEmptyString(obj.type);
		const title = asNonEmptyString(obj.title) ?? 'Untitled';
		if (!id || !type) continue;
		if (type === 'faq') {
			const itemsRaw = Array.isArray(obj.items) ? obj.items : [];
			const items: SitePageFaqItem[] = [];
			for (const item of itemsRaw) {
				if (!item || typeof item !== 'object') continue;
				const row = item as Record<string, unknown>;
				const itemId = asNonEmptyString(row.id);
				const q = asNonEmptyString(row.q) ?? '';
				const a = typeof row.a === 'string' ? row.a : '';
				if (!itemId) continue;
				items.push({ id: itemId, q, a });
			}
			out.push({ id, type: 'faq', title, items });
			continue;
		}
		if (type === 'section') {
			const body =
				typeof obj.body_markdown === 'string' ? obj.body_markdown : '';
			const tip = parseSectionTip(obj.tip);
			const block: SitePageSectionBlock = {
				id,
				type: 'section',
				title,
				body_markdown: body,
			};
			if (tip) block.tip = tip;
			out.push(block);
		}
	}
	return out;
}

export function contentBlocksFromMeta(
	meta: Record<string, unknown> | null | undefined
): SitePageContentBlock[] {
	if (!meta || typeof meta !== 'object') return [];
	return parseContentBlocks(meta.content_blocks);
}

export function mergeMetaWithContentBlocks(
	previous: Record<string, unknown> | null | undefined,
	description: string,
	blocks: SitePageContentBlock[]
): Record<string, unknown> | null {
	const meta: Record<string, unknown> = {
		...(previous && typeof previous === 'object' ? { ...previous } : {}),
	};
	const desc = description.trim();
	if (desc) meta.description = desc;
	else delete meta.description;

	const normalized = parseContentBlocks(blocks);
	if (normalized.length) meta.content_blocks = normalized;
	else delete meta.content_blocks;

	return Object.keys(meta).length ? meta : null;
}

export function storeContentBlocksSeed(
	path: string,
	blocks: SitePageContentBlock[]
): void {
	if (typeof sessionStorage === 'undefined') return;
	const normalized = parseContentBlocks(blocks);
	if (!normalized.length) return;
	sessionStorage.setItem(
		contentBlocksStorageKey(path),
		JSON.stringify(normalized)
	);
}

export function consumeContentBlocksSeed(
	path: string
): SitePageContentBlock[] | null {
	if (typeof sessionStorage === 'undefined') return null;
	const key = contentBlocksStorageKey(path);
	const raw = sessionStorage.getItem(key);
	if (raw == null) return null;
	sessionStorage.removeItem(key);
	try {
		const parsed = parseContentBlocks(JSON.parse(raw) as unknown);
		return parsed.length ? parsed : null;
	} catch {
		return null;
	}
}

/** Expand collapsed FAQ / details so seed capture includes hidden answers. */
export function expandCmsCollapsiblesForSeed(root: ParentNode): void {
	root.querySelectorAll('[data-cms-faq-answer]').forEach((el) => {
		if (el instanceof HTMLElement) {
			el.hidden = false;
			el.classList.remove('hidden');
			el.style.display = '';
		}
	});
	root.querySelectorAll('details').forEach((el) => {
		el.open = true;
	});
}

function captureSectionTip(sectionRoot: Element): SitePageSectionTip | null {
	const tipRoot = sectionRoot.querySelector('[data-cms-section-tip]');
	if (!tipRoot) return null;
	const title =
		asNonEmptyString(
			tipRoot.querySelector('[data-cms-section-tip-title]')?.textContent
		) ||
		asNonEmptyString(tipRoot.getAttribute('data-cms-section-tip-title')) ||
		'Pro Tip';
	const bodyEl =
		tipRoot.querySelector('[data-cms-section-tip-body]') || tipRoot;
	const body = (bodyEl.textContent || '').replace(/\s+\n/g, '\n').trim();
	// Prefer body without repeating the tip title heading.
	const titleEl = tipRoot.querySelector('[data-cms-section-tip-title]');
	let bodyText = body;
	if (titleEl && bodyEl === tipRoot) {
		const clone = tipRoot.cloneNode(true) as Element;
		clone.querySelector('[data-cms-section-tip-title]')?.remove();
		bodyText = (clone.textContent || '').replace(/\s+\n/g, '\n').trim();
	} else if (titleEl && bodyEl.contains(titleEl)) {
		const clone = bodyEl.cloneNode(true) as Element;
		clone.querySelector('[data-cms-section-tip-title]')?.remove();
		bodyText = (clone.textContent || '').replace(/\s+\n/g, '\n').trim();
	}
	if (!title.trim() && !bodyText.trim()) return null;
	return { title, body_markdown: bodyText };
}

/**
 * Read FAQ / section blocks marked with data-cms-* attributes on the
 * static fallback DOM (answers stay in the DOM even when collapsed).
 */
export function captureContentBlocksFromDom(
	root: ParentNode = document
): SitePageContentBlock[] {
	const blocks: SitePageContentBlock[] = [];

	const faqRoots = root.querySelectorAll('[data-cms-block-type="faq"]');
	faqRoots.forEach((faqRoot, faqIndex) => {
		const id =
			asNonEmptyString(faqRoot.getAttribute('data-cms-block-id')) ||
			`faq-${faqIndex + 1}`;
		const title =
			asNonEmptyString(faqRoot.getAttribute('data-cms-block-title')) ||
			'Frequently Asked Questions';
		const items: SitePageFaqItem[] = [];
		faqRoot.querySelectorAll('[data-cms-faq-item]').forEach((node, i) => {
			const itemId =
				asNonEmptyString(node.getAttribute('data-cms-faq-id')) || `q${i + 1}`;
			const qEl = node.querySelector('[data-cms-faq-q]');
			const aEl = node.querySelector('[data-cms-faq-a]');
			const q =
				asNonEmptyString(qEl?.textContent) ||
				asNonEmptyString(node.getAttribute('data-cms-faq-q')) ||
				'';
			const a =
				(aEl?.innerHTML || aEl?.textContent || '').trim() ||
				asNonEmptyString(node.getAttribute('data-cms-faq-a')) ||
				'';
			if (!q && !a) return;
			items.push({ id: itemId, q, a });
		});
		if (items.length) {
			blocks.push({ id, type: 'faq', title, items });
		}
	});

	root.querySelectorAll('[data-cms-block-type="section"]').forEach((node, i) => {
		const id =
			asNonEmptyString(node.getAttribute('data-cms-block-id')) ||
			`section-${i + 1}`;
		// Skip FAQ wrapper if it was also tagged as section.
		if (blocks.some((b) => b.id === id)) return;
		const title =
			asNonEmptyString(node.getAttribute('data-cms-block-title')) ||
			asNonEmptyString(
				node.querySelector('[data-cms-section-title]')?.textContent
			) ||
			'Section';
		const tip = captureSectionTip(node);
		const bodyEl =
			node.querySelector('[data-cms-section-body]') ||
			node.querySelector('.prose') ||
			node;
		const bodyClone = bodyEl.cloneNode(true) as Element;
		bodyClone
			.querySelectorAll('[data-cms-section-tip]')
			.forEach((el) => el.remove());
		bodyClone
			.querySelectorAll(
				'[data-testid="cms-edit-button"], [data-cms-block-edit], button, svg'
			)
			.forEach((el) => el.remove());
		const text = (bodyClone.textContent || '').replace(/\s+\n/g, '\n').trim();
		if (!text && !tip) return;
		const block: SitePageSectionBlock = {
			id,
			type: 'section',
			title,
			body_markdown: text,
		};
		if (tip) block.tip = tip;
		blocks.push(block);
	});

	return blocks;
}

export function adminSitePageSectionHref(opts: {
	pageId?: number | null;
	path: string;
	section: string;
	item?: string | null;
}): string {
	const params = new URLSearchParams();
	if (opts.pageId != null && Number.isFinite(opts.pageId) && opts.pageId > 0) {
		params.set('id', String(opts.pageId));
	} else {
		params.set('path', opts.path.startsWith('/') ? opts.path : `/${opts.path}`);
	}
	params.set('section', opts.section);
	if (opts.item) params.set('item', opts.item);
	params.set('seed_blocks', '1');
	return `/admin/site-pages?${params.toString()}`;
}
